import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Server, Activity, Box, Zap, Globe, TrendingUp, AlertTriangle, Circle, Plus, RefreshCw } from "lucide-react";
import { CLUSTERS as CLUSTERS_TEST } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { useCluster } from "@/components/layout/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  healthy: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  warning: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  critical: "text-rose-400 bg-rose-400/10 border-rose-400/20",
};

function ProgressBar({ value, color }) {
  return (
    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${value}%`, backgroundColor: color }} />
    </div>
  );
}

function MetricPill({ label, value, color }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</span>
        <span className="text-xs font-mono text-slate-400">{value}%</span>
      </div>
      <ProgressBar value={value} color={color} />
    </div>
  );
}

function ClusterCard({ cluster, isSelected, onSelect }) {
  return (
    <div
      data-testid={CLUSTERS_TEST.clusterCard}
      onClick={() => onSelect(cluster.id)}
      className={cn(
        "bg-slate-900/50 border rounded-md p-5 cursor-pointer transition-all duration-200 hover:bg-slate-800/50 animate-fade-in",
        isSelected ? "border-indigo-500/60" : "border-slate-800"
      )}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Server size={14} className="text-slate-400" />
            <span className="text-sm font-semibold text-slate-100" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {cluster.display_name}
            </span>
          </div>
          <p className="text-xs text-slate-500 font-mono">{cluster.provider} · {cluster.region} · k8s {cluster.k8s_version}</p>
        </div>
        <span data-testid={CLUSTERS_TEST.healthBadge} className={cn("text-[10px] px-2 py-1 rounded border font-semibold uppercase", statusStyles[cluster.status])}>
          {cluster.status}
        </span>
      </div>

      {/* Health score */}
      <div className="flex items-center gap-3 mb-4">
        <div className="text-2xl font-semibold tracking-tight" style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          color: cluster.health_score >= 90 ? "#34d399" : cluster.health_score >= 70 ? "#fbbf24" : "#f87171"
        }}>
          {cluster.health_score}
        </div>
        <div>
          <p className="text-xs text-slate-400">Health Score</p>
          <p className="text-[10px] text-slate-600 font-mono">{cluster.uptime_percent}% uptime</p>
        </div>
        {cluster.active_alerts > 0 && (
          <div className="ml-auto flex items-center gap-1 text-xs text-rose-400">
            <AlertTriangle size={11} />
            <span>{cluster.active_alerts} alerts</span>
          </div>
        )}
      </div>

      {/* Metrics */}
      <div className="space-y-2.5 mb-4">
        <MetricPill label="CPU" value={cluster.cpu_utilization}
          color={cluster.cpu_utilization > 80 ? "#f87171" : cluster.cpu_utilization > 60 ? "#fbbf24" : "#34d399"} />
        <MetricPill label="Memory" value={cluster.memory_utilization}
          color={cluster.memory_utilization > 85 ? "#f87171" : cluster.memory_utilization > 70 ? "#fbbf24" : "#34d399"} />
        <MetricPill label="Storage" value={cluster.storage_usage}
          color={cluster.storage_usage > 80 ? "#f87171" : cluster.storage_usage > 60 ? "#fbbf24" : "#6366f1"} />
      </div>

      {/* Resource counts */}
      <div className="grid grid-cols-4 gap-2 pt-3 border-t border-slate-800/60">
        {[
          { icon: Activity, label: "Nodes", value: `${cluster.nodes_ready}/${cluster.nodes_total}` },
          { icon: Box, label: "Pods", value: cluster.pods_total },
          { icon: Zap, label: "Deploys", value: cluster.deployments_total },
          { icon: Globe, label: "Svcs", value: cluster.services_count },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="text-center">
            <p className="text-sm font-semibold text-slate-200 font-mono">{value}</p>
            <p className="text-[10px] text-slate-600 mt-0.5">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Clusters() {
  const navigate = useNavigate();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const { clusterId, setClusterId } = useCluster();

  const fetchClusters = () => {
    setLoading(true);
    axios.get(`${API}/clusters`)
      .then((r) => { setClusters(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchClusters(); }, []);

  const totalAlerts = clusters.reduce((s, c) => s + c.active_alerts, 0);
  const healthyClusters = clusters.filter((c) => c.status === "healthy").length;

  return (
    <div data-testid={CLUSTERS_TEST.page} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Cluster Fleet
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {clusters.length} clusters · {healthyClusters} healthy · {totalAlerts} active alerts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchClusters} className="p-2 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800 border border-slate-700 transition-colors">
            <RefreshCw size={14} />
          </button>
          <button data-testid={CLUSTERS_TEST.addClusterBtn} onClick={() => navigate("/connect")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-50 text-slate-950 text-xs font-semibold hover:bg-slate-200 transition-colors active:scale-95">
            <Plus size={13} />
            Add Cluster
          </button>
        </div>
      </div>

      {/* Fleet Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Total Clusters", value: clusters.length, color: "text-slate-50" },
          { label: "Healthy", value: healthyClusters, color: "text-emerald-400" },
          { label: "Alerts", value: totalAlerts, color: totalAlerts > 0 ? "text-rose-400" : "text-emerald-400" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 text-center">
            <p className={cn("text-2xl font-semibold", color)} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{value}</p>
            <p className="text-xs text-slate-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Cluster Cards */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-64 bg-slate-800/50 rounded-md animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {clusters.map((c) => (
            <ClusterCard key={c.id} cluster={c} isSelected={c.id === clusterId} onSelect={setClusterId} />
          ))}
        </div>
      )}

      {/* Comparison table */}
      {!loading && clusters.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Fleet Comparison</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-800">
                  {["Cluster", "Provider", "Version", "Nodes", "Pods", "CPU %", "Memory %", "Cost/mo", "Status"].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clusters.map((c) => (
                  <tr key={c.id} onClick={() => setClusterId(c.id)}
                    className={cn("border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition-colors",
                      c.id === clusterId && "bg-indigo-500/5")}>
                    <td className="px-4 py-3 text-slate-200 font-medium">{c.display_name}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono">{c.provider}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono">{c.k8s_version}</td>
                    <td className="px-4 py-3 text-slate-300 font-mono">{c.nodes_ready}/{c.nodes_total}</td>
                    <td className="px-4 py-3 text-slate-300 font-mono">{c.pods_running}/{c.pods_total}</td>
                    <td className="px-4 py-3 font-mono" style={{ color: c.cpu_utilization > 80 ? "#f87171" : c.cpu_utilization > 60 ? "#fbbf24" : "#34d399" }}>{c.cpu_utilization}%</td>
                    <td className="px-4 py-3 font-mono" style={{ color: c.memory_utilization > 85 ? "#f87171" : c.memory_utilization > 70 ? "#fbbf24" : "#34d399" }}>{c.memory_utilization}%</td>
                    <td className="px-4 py-3 text-slate-300 font-mono">${c.cost_monthly?.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={cn("px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase", statusStyles[c.status])}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
